// ======================
// CmoSystemHealthIcon
// ======================

import { CmoSystemHealth } from "gc-web-proto/galaxycompletepb/apipb/domainpb/cmo_pb";
import { BlackTooltip } from "../../common/tooltip/ColorTooltip";
import {
    Alert,
    AlertTitle,
    Box,
    Dialog,
    DialogContent,
    IconButton,
    linearProgressClasses,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    SvgIcon,
    Theme,
    useTheme,
} from "@mui/material";
import { IoShieldCheckmarkSharp } from "react-icons/io5";
import { MdOutlineWarningAmber } from "react-icons/md";
import { PiWarningOctagonBold } from "react-icons/pi";
import { BsQuestionCircleFill } from "react-icons/bs";
import React from "react";
import { CmoHealthStatus } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/cmo_health_status_pb";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { CMC_MIGRATION_SUBROUTE, CMO_MIGRATION_SUBROUTE, getProjectSubRouteTemplate, PROJECT_SUBROUTE } from "../app/AppRoutes";
import {
    CmoMigrationSessionStatus,
    CmoMigrationSessionStatusFilter,
} from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/cmo_migration_session_status_pb";
import { CmoMigrationQosImpactLevel } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/cmo_migration_qos_impact_level_pb";
import { CmoMigrationSessionVerificationStatus } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/cmo_migration_session_verification_status_pb";
import { CmoMigrationSessionVolumeStatus } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/cmo_migration_session_volume_status_pb";
import { IconBaseProps } from "react-icons/lib/cjs/iconBase";
import { HelpIcon } from "../../common/CommonIcons";
import { DialogTopBar } from "../core/dialog/DialogComponents";
import { QueryResultWrapper } from "../core/data/QueryResultWrapper";
import { DarkFlatCard } from "../../common/card/DarkCard";
import { useGetCmoSystemHealthDetails } from "./cmo_migration_hooks";
import { useDialogState, useShouldDialogFullScreen } from "../core/dialog/GlobalDialogState";
import { CmoMigrationSessionType, CmoMigrationSessionTypeFilter } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/cmo_migration_session_type_pb";
import { ColumnSortFilterConfig, ListFilterableField } from "../core/data/ListData";
import { ListCmoMigrationSessions } from "gc-web-proto/galaxycompletepb/apipb/cmoapipb/cmo_api_pb";
import { SortState } from "../../common/table/DataTable";
import { FilterParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { useTranslation } from "react-i18next";
import { appI18n } from "../i18n/i18n";

export const getCmoSystemHealthStatusLabel = (status: CmoHealthStatus.CmoHealthStatus) => {
    switch (status) {
        case CmoHealthStatus.CmoHealthStatus.HEALTHY:
            return appI18n.t("cmoMigration:status.health.healthy");
        case CmoHealthStatus.CmoHealthStatus.WARNING:
            return appI18n.t("cmoMigration:status.health.warning");
        case CmoHealthStatus.CmoHealthStatus.FAILURE:
            return appI18n.t("cmoMigration:status.health.failure");
        case CmoHealthStatus.CmoHealthStatus.UNAVAILABLE:
            return appI18n.t("cmoMigration:status.health.unavailable");
        default:
            return appI18n.t("cmoMigration:status.health.unknown");
    }
};

interface CmoSystemHealthIconProps {
    healthStatus: CmoHealthStatus.CmoHealthStatus;
    iconProps?: IconBaseProps;
}

export const CmoSystemHealthIcon: React.FC<CmoSystemHealthIconProps> = (p) => {
    const { healthStatus, iconProps } = p;
    const theme = useTheme();

    return (
        <>
            {healthStatus === CmoHealthStatus.CmoHealthStatus.HEALTHY && <IoShieldCheckmarkSharp {...iconProps} color={theme.palette.success.main} />}
            {healthStatus === CmoHealthStatus.CmoHealthStatus.WARNING && <MdOutlineWarningAmber {...iconProps} color={theme.palette.warning.main} />}
            {healthStatus === CmoHealthStatus.CmoHealthStatus.FAILURE && <PiWarningOctagonBold {...iconProps} color={theme.palette.error.main} />}
            {(healthStatus === CmoHealthStatus.CmoHealthStatus.UNKNOWN || healthStatus === CmoHealthStatus.CmoHealthStatus.UNAVAILABLE) && (
                <BsQuestionCircleFill {...iconProps} color={theme.palette.grey["700"]} />
            )}
        </>
    );
};

// ======================
// CmoSystemHealthIcon
// ======================

interface CmoSystemHealthIconButtonProps {
    healthStatus: CmoHealthStatus.CmoHealthStatus;
    systemId: string;
    iconProps?: IconBaseProps;
}

export const CmoSystemHealthIconButton: React.FC<CmoSystemHealthIconButtonProps> = (p) => {
    const { healthStatus, systemId, iconProps } = p;
    const queryResult = useGetCmoSystemHealthDetails(systemId);
    const dialogState = useDialogState();
    const fullScreen = useShouldDialogFullScreen();
    const { t } = useTranslation("cmoMigration");

    return (
        <>
            <BlackTooltip title={t("health.viewDetails")}>
                <IconButton onClick={dialogState.open} aria-label={t("health.viewDetails")}>
                    <SvgIcon>
                        <CmoSystemHealthIcon healthStatus={healthStatus} iconProps={iconProps} />
                    </SvgIcon>
                </IconButton>
            </BlackTooltip>

            <Dialog open={dialogState.isOpen} onClose={dialogState.close} maxWidth={"md"} fullWidth fullScreen={fullScreen}>
                <DialogTopBar dialogState={dialogState} title={t("health.dialogTitle")} />
                <DialogContent>
                    <QueryResultWrapper queryResult={queryResult}>
                        {!queryResult.data?.details ? (
                            <Box>{t("health.noData")}</Box>
                        ) : (
                            <Box>
                                {queryResult.data?.details.executionError && (
                                    <Alert severity={"error"}>
                                        <AlertTitle>{t("health.executionError")}</AlertTitle>
                                        {queryResult.data?.details.executionError}
                                    </Alert>
                                )}
                                <Box pt={2}>
                                    <DarkFlatCard>
                                        <List>
                                            {queryResult.data?.details.checksList.map((check) => {
                                                return (
                                                    <ListItem key={check.key}>
                                                        <ListItemIcon>
                                                            <CmoSystemHealthIcon healthStatus={check.status} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Box>
                                                                    {check.message}
                                                                    <br />
                                                                    {check.message2}
                                                                </Box>
                                                            }
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    </DarkFlatCard>
                                </Box>
                            </Box>
                        )}
                    </QueryResultWrapper>
                </DialogContent>
            </Dialog>
        </>
    );
};

export const getCmoMigrationSessionVerificationStatusLabel = (status: CmoMigrationSessionVerificationStatus.CmoMigrationSessionVerificationStatus) => {
    switch (status) {
        case CmoMigrationSessionVerificationStatus.CmoMigrationSessionVerificationStatus.VERIFICATION_VERIFYING:
            return appI18n.t("cmoMigration:status.verification.verifying");
        case CmoMigrationSessionVerificationStatus.CmoMigrationSessionVerificationStatus.VERIFICATION_CANCELLED:
            return appI18n.t("cmoMigration:status.verification.cancelled");
        case CmoMigrationSessionVerificationStatus.CmoMigrationSessionVerificationStatus.VERIFICATION_COMPLETED:
            return appI18n.t("cmoMigration:status.verification.completed");
        case CmoMigrationSessionVerificationStatus.CmoMigrationSessionVerificationStatus.VERIFICATION_FAILED:
            return appI18n.t("cmoMigration:status.verification.failed");
        case CmoMigrationSessionVerificationStatus.CmoMigrationSessionVerificationStatus.VERIFICATION_IN_ERROR:
            return appI18n.t("cmoMigration:status.verification.inError");
        default:
            return appI18n.t("cmoMigration:status.verification.unknown");
    }
};

export const useLinearProgressStyle = (darkBackground?: boolean) => {
    const theme = useTheme();
    return {
        borderRadius: 5,
        backgroundColor: darkBackground ? theme.palette.cirrus.main : "",

        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
        },
    };
};

export const useNavigateToCmoSystemsList = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.CMO_MIGRATIONS) + `/${CMO_MIGRATION_SUBROUTE.CMO_SYSTEMS}`, { projectId });
    return () => navigate(p);
};

export const useNavigateToCmoSystemDetails = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (systemId: string) => {
        const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.CMO_MIGRATIONS) + `/${CMO_MIGRATION_SUBROUTE.CMO_SYSTEMS}/${systemId}`, {
            projectId,
        });
        navigate(p);
    };
};

export const useNavigateToCmoMigrationSessionsList = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.CMO_MIGRATIONS) + `/${CMO_MIGRATION_SUBROUTE.MIGRATION_SESSIONS}`, { projectId });
    return () => navigate(p);
};

export const useNavigateToCmoMigrationSessionDetails = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (sessionId: string) => {
        const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.CMO_MIGRATIONS) + `/${CMO_MIGRATION_SUBROUTE.MIGRATION_SESSIONS}/${sessionId}`, {
            projectId,
        });
        navigate(p);
    };
};

export const getCmoMigrationSessionStatusLabel = (status: CmoMigrationSessionStatus.CmoMigrationSessionStatus) => {
    switch (status) {
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.NEW:
            return appI18n.t("cmoMigration:status.session.new");
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.IN_PROGRESS:
            return appI18n.t("cmoMigration:status.session.inProgress");
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.SUSPENDED:
            return appI18n.t("cmoMigration:status.session.suspended");
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.COMPLETED:
            return appI18n.t("cmoMigration:status.session.completed");
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.CANCELLED:
            return appI18n.t("cmoMigration:status.session.cancelled");
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.FAILED:
            return appI18n.t("cmoMigration:status.session.failed");
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.PENDING_COMPLETE:
            return appI18n.t("cmoMigration:status.session.pendingComplete");
        case CmoMigrationSessionStatus.CmoMigrationSessionStatus.IN_ERROR:
            return appI18n.t("cmoMigration:status.session.inError");
        default:
            return appI18n.t("cmoMigration:status.session.unknown");
    }
};

export const getCmoMigrationSessionVolumeStatusLabel = (status: CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus) => {
    switch (status) {
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.NEW:
            return appI18n.t("cmoMigration:status.sessionVolume.new");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.IN_PROGRESS:
            return appI18n.t("cmoMigration:status.sessionVolume.inProgress");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.SUSPENDED:
            return appI18n.t("cmoMigration:status.sessionVolume.suspended");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.COMPLETED:
            return appI18n.t("cmoMigration:status.sessionVolume.completed");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.CANCELLED:
            return appI18n.t("cmoMigration:status.sessionVolume.cancelled");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.FAILED:
            return appI18n.t("cmoMigration:status.sessionVolume.failed");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.PENDING_COMPLETE:
            return appI18n.t("cmoMigration:status.sessionVolume.pendingComplete");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.IN_ERROR:
            return appI18n.t("cmoMigration:status.sessionVolume.inError");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.COPYING:
            return appI18n.t("cmoMigration:status.sessionVolume.copying");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.YIELDING:
            return appI18n.t("cmoMigration:status.sessionVolume.yielding");
        case CmoMigrationSessionVolumeStatus.CmoMigrationSessionVolumeStatus.WAITING:
            return appI18n.t("cmoMigration:status.sessionVolume.waiting");
        default:
            return appI18n.t("cmoMigration:status.sessionVolume.unknown");
    }
};

export const getCmoMigrationSessionStatusColor = (status: CmoMigrationSessionStatus.CmoMigrationSessionStatus, theme: Theme) => {
    if (status === CmoMigrationSessionStatus.CmoMigrationSessionStatus.NEW) {
        return "white";
    }
    if (status === CmoMigrationSessionStatus.CmoMigrationSessionStatus.IN_PROGRESS) {
        return theme.palette.primary.main;
    }
    if (status === CmoMigrationSessionStatus.CmoMigrationSessionStatus.PENDING_COMPLETE) {
        return theme.palette.warning.light;
    }
    if (status === CmoMigrationSessionStatus.CmoMigrationSessionStatus.COMPLETED) {
        return theme.palette.success.main;
    }
    if (status === CmoMigrationSessionStatus.CmoMigrationSessionStatus.FAILED || status === CmoMigrationSessionStatus.CmoMigrationSessionStatus.IN_ERROR) {
        return theme.palette.error.main;
    }
    return theme.palette.grey[500];
};

export const getCmoMigrationSessionTypeLabel = (sessionType: CmoMigrationSessionType.CmoMigrationSessionType) => {
    switch (sessionType) {
        case CmoMigrationSessionType.CmoMigrationSessionType.LOCAL:
            return appI18n.t("cmoMigration:status.sessionType.local");
        case CmoMigrationSessionType.CmoMigrationSessionType.REMOTE:
            return appI18n.t("cmoMigration:status.sessionType.remote");
        case CmoMigrationSessionType.CmoMigrationSessionType.SECURE_ERASE:
            return appI18n.t("cmoMigration:status.sessionType.secureErase");
        case CmoMigrationSessionType.CmoMigrationSessionType.SWINGBOX:
            return appI18n.t("cmoMigration:status.sessionType.swingbox");
        case CmoMigrationSessionType.CmoMigrationSessionType.SWINGBOX_IMPORT:
            return appI18n.t("cmoMigration:status.sessionType.swingboxImport");
        default:
            return appI18n.t("cmoMigration:status.sessionType.unknown");
    }
};

export const getCmoMigrationQosImpactLevelLabel = (impactLevel: string | CmoMigrationQosImpactLevel.CmoMigrationQosImpactLevel) => {
    switch (impactLevel) {
        case CmoMigrationQosImpactLevel.CmoMigrationQosImpactLevel.MINIMAL:
        case "MINIMAL":
            return appI18n.t("cmoMigration:status.qosImpact.minimal");
        case CmoMigrationQosImpactLevel.CmoMigrationQosImpactLevel.MODERATE:
        case "MODERATE":
            return appI18n.t("cmoMigration:status.qosImpact.moderate");
        case CmoMigrationQosImpactLevel.CmoMigrationQosImpactLevel.AGGRESSIVE:
        case "AGGRESSIVE":
            return appI18n.t("cmoMigration:status.qosImpact.aggressive");
        default:
            return appI18n.t("cmoMigration:status.qosImpact.unknown");
    }
};

export const getCmoMigrationSessionsListSortFilterConfig = (): ColumnSortFilterConfig<
    ListCmoMigrationSessions.Request,
    ListCmoMigrationSessions.Request.Sort,
    any,
    any
> => {
    return {
        sort: {
            defaultSort: {
                fieldId: ListCmoMigrationSessions.Request.Sort.Field.CREATED_AT,
                label: appI18n.t("cmoMigration:sessionListControls.sort.creationTime"),
            },
            defaultSortDesc: true,
            sortFields: [
                { fieldId: ListCmoMigrationSessions.Request.Sort.Field.CREATED_AT, label: appI18n.t("cmoMigration:sessionListControls.sort.creationTime") },
                { fieldId: ListCmoMigrationSessions.Request.Sort.Field.STARTED_AT, label: appI18n.t("cmoMigration:sessionListControls.sort.startTime") },
                {
                    fieldId: ListCmoMigrationSessions.Request.Sort.Field.AVERAGE_THROUGHPUT,
                    label: appI18n.t("cmoMigration:sessionListControls.sort.averageMigrationRate"),
                },
                {
                    fieldId: ListCmoMigrationSessions.Request.Sort.Field.CURRENT_THROUGHPUT,
                    label: appI18n.t("cmoMigration:sessionListControls.sort.currentMigrationRate"),
                },
                { fieldId: ListCmoMigrationSessions.Request.Sort.Field.MIGRATION_STATUS, label: appI18n.t("cmoMigration:sessionListControls.sort.status") },
                {
                    fieldId: ListCmoMigrationSessions.Request.Sort.Field.AVERAGE_TRANSFER_THROUGHPUT,
                    label: appI18n.t("cmoMigration:sessionListControls.sort.averageTransferRate"),
                },
                {
                    fieldId: ListCmoMigrationSessions.Request.Sort.Field.PROGRESS_PERCENTAGE,
                    label: appI18n.t("cmoMigration:sessionListControls.sort.progress"),
                },
                {
                    fieldId: ListCmoMigrationSessions.Request.Sort.Field.THIN_PERCENTAGE,
                    label: appI18n.t("cmoMigration:sessionListControls.sort.thinDataRatio"),
                },
                { fieldId: ListCmoMigrationSessions.Request.Sort.Field.TOTAL_CAPACITY, label: appI18n.t("cmoMigration:sessionListControls.sort.capacity") },
                { fieldId: ListCmoMigrationSessions.Request.Sort.Field.TOTAL_VOLUMES, label: appI18n.t("cmoMigration:sessionListControls.sort.volumes") },
            ],
            getSortParam: (s: SortState) => new ListCmoMigrationSessions.Request.Sort().setField(s.field.fieldId).setDesc(s.descending.value),
        },
        filter: [
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.GLOBAL_SESSION_ID,
                label: appI18n.t("cmoMigration:sessionListControls.filter.globalSessionId"),
                filterType: "string",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setGlobalSessionId(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.LOCAL_SESSION_ID,
                label: appI18n.t("cmoMigration:sessionListControls.filter.localSessionId"),
                filterType: "simpleNumber",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.SimpleNumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setLocalSessionId(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.DESCRIPTION,
                label: appI18n.t("cmoMigration:sessionListControls.filter.description"),
                filterType: "string",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setDescription(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.STATUS,
                label: appI18n.t("cmoMigration:sessionListControls.filter.status"),
                filterType: "enum",
                enumLabel: appI18n.t("cmoMigration:sessionListControls.filter.statusValue"),
                enumValuesList: Object.values(CmoMigrationSessionStatus.CmoMigrationSessionStatus),
                getFormValueDisplayLabel: (v: CmoMigrationSessionStatus.CmoMigrationSessionStatus) => getCmoMigrationSessionStatusLabel(v),
                createParamFromFormValue: (v: CmoMigrationSessionStatus.CmoMigrationSessionStatus) =>
                    new CmoMigrationSessionStatusFilter().setHas(new CmoMigrationSessionStatus().setValue(v)),
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: CmoMigrationSessionStatusFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setStatus(f));
                },
            } as ListFilterableField<ListCmoMigrationSessions.Request, CmoMigrationSessionStatusFilter, CmoMigrationSessionStatus.CmoMigrationSessionStatus>,
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.TYPE,
                label: appI18n.t("cmoMigration:sessionListControls.filter.type"),
                filterType: "enum",
                enumLabel: appI18n.t("cmoMigration:sessionListControls.filter.migrationType"),
                enumValuesList: Object.values(CmoMigrationSessionType.CmoMigrationSessionType),
                getFormValueDisplayLabel: (v: CmoMigrationSessionType.CmoMigrationSessionType) => getCmoMigrationSessionTypeLabel(v),
                createParamFromFormValue: (v: CmoMigrationSessionType.CmoMigrationSessionType) =>
                    new CmoMigrationSessionTypeFilter().setHas(new CmoMigrationSessionType().setValue(v)),
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: CmoMigrationSessionTypeFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setType(f));
                },
            } as ListFilterableField<ListCmoMigrationSessions.Request, CmoMigrationSessionTypeFilter, CmoMigrationSessionType.CmoMigrationSessionType>,
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.TOTAL_VOLUMES,
                label: appI18n.t("cmoMigration:sessionListControls.filter.volumes"),
                filterType: "number",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setTotalVolumes(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.TOTAL_CAPACITY,
                label: appI18n.t("cmoMigration:sessionListControls.filter.capacity"),
                filterType: "number",
                numType: "capacity",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setTotalCapacity(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.CREATED_AT,
                label: appI18n.t("cmoMigration:sessionListControls.filter.created"),
                filterType: "date",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.DateFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setCreatedAt(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.STARTED_AT,
                label: appI18n.t("cmoMigration:sessionListControls.filter.started"),
                filterType: "date",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.DateFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setStartedAt(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.LABEL,
                label: appI18n.t("cmoMigration:sessionListControls.filter.label"),
                filterType: "string",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setLabel(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.ACTIVE_MIGRATION_TIME_PERCENTAGE,
                label: appI18n.t("cmoMigration:sessionListControls.filter.dutyCycle"),
                filterType: "number",
                numType: "percentage",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setActiveMigrationTimePercentage(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.THIN_PERCENTAGE,
                label: appI18n.t("cmoMigration:sessionListControls.filter.thinDataRatio"),
                filterType: "number",
                numType: "percentage",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setThinPercentage(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.PROGRESS_PERCENTAGE,
                label: appI18n.t("cmoMigration:sessionListControls.filter.progress"),
                filterType: "number",
                numType: "percentage",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setProgressPercentage(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.AVERAGE_THROUGHPUT,
                label: appI18n.t("cmoMigration:sessionListControls.filter.averageMigrationRate"),
                filterType: "number",
                numType: "throughput",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setAverageThroughput(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.AVERAGE_TRANSFER_THROUGHPUT,
                label: appI18n.t("cmoMigration:sessionListControls.filter.averageTransferRate"),
                filterType: "number",
                numType: "throughput",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setAverageTransferThroughput(f));
                },
            },
            {
                fieldId: ListCmoMigrationSessions.Request.Filter.FieldCase.SYSTEM_NAME,
                label: appI18n.t("cmoMigration:sessionListControls.filter.systemName"),
                filterType: "string",
                addFilterToRequest: (r: ListCmoMigrationSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListCmoMigrationSessions.Request.Filter().setSystemName(f));
                },
            },
        ],
    };
};
